'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { MobileTopBar } from '@/app/components/layouts/top-navigation/shared/mobile-top-bar';
import { SubMenuRenderLayout } from '@/app/components/layouts/top-navigation/sub-menu/render-layout';
import { useMountTransition } from '@/app/hooks/useMountTransition';
import type { NavItemType } from '@/app/types/top-navigation';
import { cn } from '@/app/utils/cn';
import { safeCall } from '@/app/utils/safeCall';
const Backdrop = dynamic(() => import('@/app/components/overlays/backdrop').then(mod => mod.Backdrop), {
  ssr: false
});
interface SubMenuProps {
  logo?: ReactNode;
  isOpen?: boolean;
  activeItem?: NavItemType;
  isDesktop?: boolean;
  onSetActiveItem?: (item?: NavItemType) => void;
  onClose?: () => void;
}
const SUBMENU_ID = 'menu_submenu';
export const SubMenu = t0 => {
  const $ = _c(54);
  const {
    logo,
    isOpen,
    activeItem,
    isDesktop: t1,
    onSetActiveItem,
    onClose
  } = t0;
  const isDesktop = t1 === undefined ? false : t1;
  const menuRef = useRef(null);
  const [desktopActiveItem, setDesktopActiveItem] = useState(undefined);
  const [isHover, setIsHover] = useState(false);
  const [hasContentMounted, setHasContentMounted] = useState(false);
  const t2 = !!isOpen && !!hasContentMounted;
  let t3;
  if ($[0] !== t2) {
    t3 = {
      isMount: t2,
      targetId: SUBMENU_ID,
      ref: menuRef,
      animateClassName: "opacity-100",
      baseClassName: "opacity-0 transition-opacity duration-300 ease-out"
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    className: transitionClass,
    transitionState,
    isTransitioning
  } = useMountTransition(t3);
  let t4;
  if ($[2] !== onSetActiveItem) {
    t4 = () => {
      safeCall(onSetActiveItem, undefined);
    };
    $[2] = onSetActiveItem;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const clearSelectItem = t4;
  let t5;
  if ($[4] !== clearSelectItem || $[5] !== isDesktop || $[6] !== onClose) {
    t5 = () => {
      safeCall(onClose);
      if (!isDesktop) {
        clearSelectItem();
      } else {
        setIsHover(false);
      }
    };
    $[4] = clearSelectItem;
    $[5] = isDesktop;
    $[6] = onClose;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const closeActiveMenu = t5;
  let t6;
  let t7;
  if ($[8] !== activeItem || $[9] !== isDesktop || $[10] !== isOpen || $[11] !== isTransitioning) {
    t6 = () => {
      if (!isDesktop) {
        return;
      }
      if (isOpen && activeItem) {
        setDesktopActiveItem(activeItem);
      }
      if (!isOpen && !isTransitioning) {
        setHasContentMounted(false);
        setDesktopActiveItem(undefined);
      }
    };
    t7 = [isOpen, activeItem, isDesktop, isTransitioning];
    $[8] = activeItem;
    $[9] = isDesktop;
    $[10] = isOpen;
    $[11] = isTransitioning;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  if (!isDesktop) {
    const t8 = activeItem?.label;
    let t9;
    if ($[14] !== clearSelectItem || $[15] !== closeActiveMenu || $[16] !== logo || $[17] !== t8) {
      t9 = <MobileTopBar logo={logo} activeLabel={t8} onBack={clearSelectItem} onClose={closeActiveMenu} />;
      $[14] = clearSelectItem;
      $[15] = closeActiveMenu;
      $[16] = logo;
      $[17] = t8;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== isOpen) {
      t10 = () => {
        if (isOpen) {
          setHasContentMounted(true);
        }
      };
      $[19] = isOpen;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== activeItem || $[22] !== onClose || $[23] !== t10) {
      t11 = <div data-lenis-prevent={true} className="scroll-allow nav-scrollbar absolute inset-0 top-14 w-full bg-white"><SubMenuRenderLayout activeItem={activeItem} onReady={t10} onClose={onClose} /></div>;
      $[21] = activeItem;
      $[22] = onClose;
      $[23] = t10;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    let t12;
    if ($[25] !== t11 || $[26] !== t9) {
      t12 = <>{t9}{t11}</>;
      $[25] = t11;
      $[26] = t9;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    return t12;
  }
  const displayedItem = isOpen && activeItem ? activeItem : desktopActiveItem;
  if (!isTransitioning && !displayedItem) {
    return null;
  }
  let t8;
  if ($[28] !== closeActiveMenu || $[29] !== displayedItem || $[30] !== isHover || $[31] !== transitionClass || $[32] !== transitionState) {
    t8 = transitionState !== "exit" && <Backdrop className={cn("fixed inset-0", transitionClass, {
      "duration-800": transitionState === "animate",
      "pointer-events-none opacity-0": !displayedItem
    })} disabledAnimate={true} onClick={closeActiveMenu} onMouseEnter={isHover ? closeActiveMenu : undefined} />;
    $[28] = closeActiveMenu;
    $[29] = displayedItem;
    $[30] = isHover;
    $[31] = transitionClass;
    $[32] = transitionState;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  const t9 = !!displayedItem;
  const t10 = !displayedItem;
  const t11 = !isOpen;
  let t12;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t9 || $[37] !== transitionClass) {
    t12 = cn("absolute top-0 w-full bg-white pt-24 pb-10", {
      "min-h-94": t9,
      "pointer-events-none opacity-0": t10,
      "pointer-events-none": t11
    }, transitionClass);
    $[34] = t10;
    $[35] = t11;
    $[36] = t9;
    $[37] = transitionClass;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== isOpen) {
    t13 = isOpen ? () => setIsHover(true) : undefined;
    $[39] = isOpen;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== isOpen) {
    t14 = () => {
      if (isOpen) {
        setHasContentMounted(true);
      }
    };
    $[41] = isOpen;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== closeActiveMenu || $[44] !== displayedItem || $[45] !== t14) {
    t15 = <SubMenuRenderLayout activeItem={displayedItem} isDesktop={true} onReady={t14} onClose={closeActiveMenu} />;
    $[43] = closeActiveMenu;
    $[44] = displayedItem;
    $[45] = t14;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== t12 || $[48] !== t13 || $[49] !== t15) {
    t16 = <aside ref={menuRef} id={SUBMENU_ID} className={t12} onMouseEnter={t13}>{t15}</aside>;
    $[47] = t12;
    $[48] = t13;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== t16 || $[52] !== t8) {
    t17 = <>{t8}{t16}</>;
    $[51] = t16;
    $[52] = t8;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  return t17;
};